import { useState } from "react";
import { useSnackbar } from "notistack";
import axios from "../../../../configApi/axiosConfig";
import { APIURL } from "../../../../configApi/apiConfig";
import uploadFile from "../../../../hooks/uploadFile";
import { DOCUMENTO_IDS_BS, isDocumentoDigital } from "../constants/documentos-bs";

export function useUploadBs({
  activeTab,
  files,
  setFiles,
  setFilePreviews,
  clientInfo,
  userData,
  showOnlyCorrections,
  filesToCorrect,
  setRefreshFiles,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const [showFileInput, setShowFileInput] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [observacion, setObservacion] = useState({});

  const verificarDocumento = async (idCreSolicitudWeb, tipoDocumento) => {
    try {
      const resp = await axios.get(APIURL.getCheckDocumento(idCreSolicitudWeb, tipoDocumento));
      return resp.data;
    } catch {
      return false;
    }
  };

  const handleFileChange = (e, field) => {
    if (isDocumentoDigital(field)) {
      enqueueSnackbar("Este documento es generado automáticamente.", { variant: "info" });
      return;
    }

    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) {
      enqueueSnackbar("No se ha seleccionado ningún archivo.", { variant: "error" });
      return;
    }

    if (files[field]?.length > 0 && clientInfo.idEstadoVerificacionDocumental === 1) {
      enqueueSnackbar(`Ya tienes un archivo en "${field}". Elimínalo primero.`, { variant: "error" });
      return;
    }

    if (showOnlyCorrections) {
      const tieneActivo = files[activeTab]?.some((f) => f.estado !== 5);
      if (filesToCorrect.includes(activeTab) && tieneActivo) {
        enqueueSnackbar("No puedes subir archivos hasta corregir los documentos pendientes.", { variant: "error" });
        return;
      }
    }

    setFiles((prev) => ({ ...prev, [field]: [...(prev[field] || []), ...selectedFiles] }));
    setShowFileInput(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDocumentoDigital(activeTab)) return;

    const physicalFile = files[activeTab]?.find((f) => !f?.url);
    if (!physicalFile) {
      enqueueSnackbar(`Selecciona un archivo para "${activeTab}"`, { variant: "error" });
      return;
    }

    const observacionText = observacion[activeTab] ?? "";
    if (observacionText && observacionText.length < 10) {
      enqueueSnackbar("La observación debe tener al menos 10 caracteres.", { variant: "error" });
      return;
    }

    const tipoDocumento = DOCUMENTO_IDS_BS[activeTab] ?? null;

    setIsUploading(true);
    try {
      const documentoExiste = await verificarDocumento(clientInfo.id, tipoDocumento);
      if (documentoExiste?.exists) {
        enqueueSnackbar("Ya existe un documento cargado para este campo.", { variant: "error" });
        return;
      }

      if (
        clientInfo.idEstadoVerificacionDocumental === 1 ||
        clientInfo.idEstadoVerificacionDocumental === 3
      ) {
        const uploadResp = await uploadFile(
          physicalFile,
          clientInfo.almacen,
          clientInfo.cedula,
          clientInfo.NumeroSolicitud,
          activeTab
        );

        if (!uploadResp?.url) {
          enqueueSnackbar("Error al subir el archivo.", { variant: "error" });
          return;
        }

        if (!tipoDocumento) {
          enqueueSnackbar("Error: tipo de documento no reconocido.", { variant: "error" });
          return;
        }

        const apiResp = await axios.post(
          APIURL.post_documentos(),
          {
            idCre_SolicitudWeb: clientInfo.id,
            idTipoDocumentoWEB: tipoDocumento,
            RutaDocumento: uploadResp.url,
            Observacion: observacionText,
            Usuario: userData.Nombre,
            IdUsuario: userData.idUsuario,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        if (apiResp.status === 201 || apiResp.data?.status === "success") {
          enqueueSnackbar("Documento guardado correctamente.", { variant: "success" });
          setShowFileInput(false);
          setRefreshFiles((prev) => !prev);
        } else {
          enqueueSnackbar("Error al guardar en base de datos.", { variant: "error" });
        }
      }
    } catch (err) {
      enqueueSnackbar("Error en la solicitud. Inténtalo de nuevo.", { variant: "error" });
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenDeleteConfirmation = (field, index) => {
    const file = files[field]?.[index];
    if (file?.esDigital) {
      enqueueSnackbar("Los documentos generados automáticamente no pueden eliminarse.", { variant: "info" });
      return;
    }
    setFileToDelete({ field, index, id: file?.idDocumentoSolicitudWeb });
    setShowConfirmDeleteModal(true);
  };

  const closeDeleteConfirmation = () => {
    setFileToDelete(null);
    setShowConfirmDeleteModal(false);
  };

  const handleRemoveFile = async () => {
    if (!fileToDelete) return;
    const { field, index, id } = fileToDelete;

    if (clientInfo.idEstadoVerificacionDocumental === 1) {
      try {
        await axios.patch(APIURL.patch_documentos(id), { idEstadoDocumento: 6 });
        enqueueSnackbar("Documento eliminado.", { variant: "success" });
      } catch {
        enqueueSnackbar("Error al eliminar el documento.", { variant: "error" });
        return;
      }
    } else if (
      clientInfo.idEstadoVerificacionDocumental === 2 ||
      clientInfo.idEstadoVerificacionDocumental === 3
    ) {
      if (id) {
        try {
          await axios.patch(APIURL.patch_documentos(id), { idEstadoDocumento: 5 });
          enqueueSnackbar("Documento eliminado.", { variant: "success" });
          setRefreshFiles((prev) => !prev);
        } catch {
          enqueueSnackbar("Error al eliminar el documento.", { variant: "error" });
          return;
        }
      }
    }

    setFiles((prev) => {
      const updated = { ...prev };
      updated[field] = updated[field].filter((_, i) => i !== index);
      if (!updated[field].length) delete updated[field];
      return updated;
    });
    setFilePreviews((prev) => {
      const updated = { ...prev };
      updated[field] = updated[field]?.filter((_, i) => i !== index) ?? [];
      if (!updated[field].length) delete updated[field];
      return updated;
    });

    closeDeleteConfirmation();
  };

  return {
    isUploading,
    showFileInput, setShowFileInput,
    showConfirmDeleteModal,
    fileToDelete,
    observacion, setObservacion,
    handleFileChange,
    handleSubmit,
    handleOpenDeleteConfirmation,
    closeDeleteConfirmation,
    handleRemoveFile,
  };
}
